import { supabase } from "../supabase";
import { safeNum, formatLocalDate, pgrestQuote, escapeFilterValue } from "./helpers";
import { pmError } from "./errors";
import { logAudit } from "./audit";
import {
  autoPostJournalEntry, getPropertyClassId, deactivateTenantRecurring, tenantOwedFromGL,
} from "./accounting";

// Archiving a tenant, in ONE place.
//
// This procedure used to live inside Tenants.js's deleteTenant, reachable
// only by an admin clicking delete on the Tenants page. A non-admin clicking
// the same button files a property_change_request instead, for an admin to
// approve -- and the approval handler in Properties.js had no branch for it.
// `delete_tenant` appeared exactly once in the whole repository: at the
// insert. Nothing read it. So a staff request was filed, approved, and
// audit-logged as approved while the tenant stayed exactly where it was.
//
// The bug was not that the approval branch was missing. It was that the work
// lived in a component, where the only way to reuse it was to write it again
// -- and a second implementation of "archive a tenant" would have drifted
// from this one immediately. Everything below is load-bearing and was learned
// the hard way: the multi-unit slot handling (blanket-clearing every tenant
// slot once wiped siblings A, C, D and E when B was archived), the
// tenant_id-scoped lease termination (an .or() on name OR property once
// terminated every active lease at a multi-unit address), and the write-off's
// per-tenant AR sub-account (crediting the bare 1100 parent left the debt
// showing on the tenant's ledger after the books had written it off).
//
// So there is one copy, both paths call it, and neither can drift.
//
// The caller owns the guards and confirmations -- balance checks, unreturned
// deposits, "are you sure" -- because those differ by who is asking. The
// caller also owns its own UI refresh. This owns the data.
// What archiving this tenant would mean for the books, BEFORE anything is
// done (audit MONEY-13; Sahil 2026-10-04: "Write off automatically but ask
// first"). From the GL -- the tenant's own receivable, posted entries -- not
// the stored balance. { owed, credit, error }: owed > 0 is what a write-off
// would post; credit > 0 is money owed TO the tenant (a refund), which is
// never written off.
export async function archiveTenantBalance(companyId, tenantId) {
  const owedNow = await tenantOwedFromGL(companyId, tenantId);
  if (owedNow === null) return { owed: 0, credit: 0, error: "the tenant's balance could not be read from the books" };
  const owed = Math.round(owedNow * 100) / 100;
  return { owed: owed > 0.005 ? owed : 0, credit: owed < -0.005 ? -owed : 0, error: null };
}

// writeOff: { approved: true, amount, reason } -- the person saw the amount
// and agreed. Without it a tenant who owes money is NOT archived (returns
// { ok: false, needsWriteOff: amount }); a tenant in credit never is
// ({ ok: false, credit }). The amount approved must still be what is owed
// when the archive runs.
export async function archiveTenant({
  companyId, tenantId, name, archivedBy, userRole, onToast, writeOff = null,
}) {
  if (!companyId || !tenantId) {
    return { ok: false, error: new Error("archiveTenant needs a company and a tenant id") };
  }
  const toast = typeof onToast === "function" ? onToast : () => {};
  const bal = await archiveTenantBalance(companyId, tenantId);
  if (bal.error) return { ok: false, error: new Error(bal.error) };
  if (bal.credit > 0) return { ok: false, credit: bal.credit, error: new Error(`${name} has a credit of $${bal.credit.toFixed(2)} — refund it or apply it before archiving`) };
  if (bal.owed > 0 && !(writeOff && writeOff.approved === true && Math.abs(safeNum(writeOff.amount) - bal.owed) < 0.005)) {
    return { ok: false, needsWriteOff: bal.owed, error: new Error(`${name} owes $${bal.owed.toFixed(2)} — confirm the write-off first`) };
  }
  // Get tenant's property before archiving for cascade updates
  const { data: tenantDetail } = await supabase.from("tenants").select("property, balance").eq("id", tenantId).eq("company_id", companyId).maybeSingle();
  const tenantProperty = tenantDetail?.property;
  // The write-off the person confirmed (archiveTenant refuses above without
  // it): DR Bad Debt Expense / CR the tenant's OWN receivable, one per
  // tenant per month (WOFF-T<tenant>-<yyyymm>), with their reason.
  let wroteOff = false;
  if (bal.owed > 0) {
    const classId = tenantProperty ? await getPropertyClassId(tenantProperty, companyId) : null;
    const { data: arRows } = await supabase.from("acct_accounts").select("id, name, is_active, code")
      .eq("company_id", companyId).eq("tenant_id", tenantId).eq("type", "Asset")
      .order("code", { ascending: true, nullsFirst: false });
    const ar = (arRows || []).find(a => a.is_active !== false) || (arRows || [])[0] || null;
    if (!ar) {
      toast("Could not find " + name + "'s receivable account — the balance was not written off and the tenant was not archived.", "error");
      return { ok: false, error: new Error("no receivable account") };
    }
    const today = formatLocalDate(new Date());
    const reason = String(writeOff.reason || "").trim();
    const woffJeId = await autoPostJournalEntry({ companyId, date: today,
      description: "AR write-off — tenant archived — " + name + (reason ? " — " + reason : ""),
      reference: "WOFF-T" + tenantId + "-" + today.slice(0, 7).replace("-", ""), property: tenantProperty || "",
      lines: [
        { account_id: "5500", account_name: "Bad Debt Expense", debit: bal.owed, credit: 0, class_id: classId, memo: "Write-off — " + name + (reason ? ": " + reason : "") },
        { account_id: ar.id, account_name: ar.name || "AR - " + name, debit: 0, credit: bal.owed, class_id: classId, memo: "AR write-off — " + name },
      ],
    });
    if (!woffJeId) {
      toast("The write-off for " + name + " could not be posted — the tenant was not archived.", "error");
      return { ok: false, error: new Error("write-off not posted") };
    }
    wroteOff = true;
    // The line on the tenant's own receivable recomputes tenants.balance.
  }
  // Record what is about to be switched off, so Restore can put exactly
  // that back (plan 5.4). If it cannot be recorded, nothing is archived.
  const { error: recErr } = await supabase.rpc("record_tenant_teardown", { p_company_id: companyId, p_tenant_id: tenantId });
  if (recErr) { pmError("PM-3003", { raw: recErr, context: "record tenant teardown" }); return { ok: false, error: recErr }; }
  // Soft-delete: archive instead of permanent deletion
  const { error: archiveErr } = await supabase.from("tenants").update({
  archived_at: new Date().toISOString(),
  archived_by: archivedBy,
  lease_status: "past"
  }).eq("id", tenantId).eq("company_id", companyId);
  if (archiveErr) { pmError("PM-3003", { raw: archiveErr, context: "archive tenant" }); return { ok: false, error: archiveErr }; }
  // Update property when tenant archived. At a multi-unit address we must
  // NOT blanket-clear every tenant slot — that used to wipe siblings A, C,
  // D, E when B was archived. Only mark the property vacant (and clear
  // lease/rent fields) when this was the sole remaining tenant. Otherwise
  // clear just the slot this tenant occupied.
  if (tenantProperty) {
  const { data: otherTenants } = await supabase.from("tenants").select("id").eq("company_id", companyId).eq("property", tenantProperty).neq("id", tenantId).is("archived_at", null);
  const hasOthers = (otherTenants || []).length > 0;
  if (!hasOthers) {
  const { error: propErr } = await supabase.from("properties").update({ status: "vacant", tenant: "", tenant_2: "", tenant_2_email: "", tenant_2_phone: "", tenant_3: "", tenant_3_email: "", tenant_3_phone: "", tenant_4: "", tenant_4_email: "", tenant_4_phone: "", tenant_5: "", tenant_5_email: "", tenant_5_phone: "", lease_end: null, lease_start: "", rent: null, security_deposit: null }).eq("company_id", companyId).eq("address", tenantProperty);
  if (propErr) pmError("PM-2002", { raw: propErr, context: "update property to vacant", silent: true });
  } else {
  // Find which slot this tenant occupies and clear only that one.
  const { data: propRow } = await supabase.from("properties").select("tenant, tenant_2, tenant_3, tenant_4, tenant_5").eq("company_id", companyId).eq("address", tenantProperty).maybeSingle();
  if (propRow) {
  const slotUpdate = {};
  if (propRow.tenant === name) slotUpdate.tenant = "";
  else if (propRow.tenant_2 === name) Object.assign(slotUpdate, { tenant_2: "", tenant_2_email: "", tenant_2_phone: "" });
  else if (propRow.tenant_3 === name) Object.assign(slotUpdate, { tenant_3: "", tenant_3_email: "", tenant_3_phone: "" });
  else if (propRow.tenant_4 === name) Object.assign(slotUpdate, { tenant_4: "", tenant_4_email: "", tenant_4_phone: "" });
  else if (propRow.tenant_5 === name) Object.assign(slotUpdate, { tenant_5: "", tenant_5_email: "", tenant_5_phone: "" });
  if (Object.keys(slotUpdate).length > 0) {
  const { error: propErr } = await supabase.from("properties").update(slotUpdate).eq("company_id", companyId).eq("address", tenantProperty);
  if (propErr) pmError("PM-2002", { raw: propErr, context: "clear tenant slot on archive", silent: true });
  }
  }
  }
  }
  // Terminate active leases for THIS tenant only. Scoping by tenant_id is
  // authoritative; fall back to (tenant_name AND property) when the lease
  // row predates tenant_id backfill. Previous .or() scoped by name OR
  // property, which terminated every other active lease at a multi-unit
  // address when one tenant was archived.
  let leaseTermQ = supabase.from("leases").update({ status: "terminated", archived_at: new Date().toISOString() }).eq("company_id", companyId).eq("status", "active");
  leaseTermQ = tenantId ? leaseTermQ.or(`tenant_id.eq.${tenantId},and(tenant_name.eq.${pgrestQuote(name)},property.eq.${pgrestQuote(tenantProperty || "")})`) : leaseTermQ.eq("tenant_name", name).eq("property", tenantProperty || "");
  const { error: leaseErr } = await leaseTermQ;
  if (leaseErr) pmError("PM-3004", { raw: leaseErr, context: "terminate leases on archive", silent: true });
  // Archive autopay schedules for this tenant
  // By tenant_id (a renamed tenant's schedule no longer matched by name and
  // kept charging, audit LIFE-13), and by name + property for old rows.
  await supabase.from("autopay_schedules").update({ enabled: false }).eq("company_id", companyId).eq("tenant_id", tenantId);
  await supabase.from("autopay_schedules").update({ enabled: false }).eq("company_id", companyId).eq("tenant", name).eq("property", tenantProperty);
  // Stop the tenant's recurring rent. Without this an archived tenant went on
  // accruing rent every month (autoPostRecurringEntries also refuses to bill
  // an archived tenant, but the schedule should not stay live either).
  const recStop = await deactivateTenantRecurring(companyId, tenantId);
  if (!recStop.ok) toast("Tenant archived, but their recurring rent entry could not be stopped — please deactivate it in Accounting.", "error");
  // Deactivate tenant AR sub-accounts
  // ...only once the books show nothing left on them (after any write-off);
  // a ledger with a posted balance stays open (audit MONEY-12).
  const left = await tenantOwedFromGL(companyId, tenantId);
  if (left !== null && Math.abs(left) < 0.005) {
    await supabase.from("acct_accounts").update({ is_active: false }).eq("company_id", companyId).eq("tenant_id", tenantId);
  }
  logAudit("delete", "tenants", `Deleted tenant: ${name} (property→vacant, lease terminated, autopay disabled${wroteOff ? `, $${bal.owed.toFixed(2)} written off: ${String(writeOff.reason || "").trim()}` : ""})`, tenantId, archivedBy, userRole, companyId);
  return { ok: true, property: tenantProperty || null, wroteOff: wroteOff ? bal.owed : 0 };
}

// The signed-in tenant's own record (audit FE-1, plan 5.5). By the id the
// database resolves for this login (get_tenant_id), else the newest LIVE
// tenant with this email -- an email on two rows (an archived one, a
// re-created tenant) used to make the lookup fail and the portal say there
// was no tenant record at all. A failure is returned as an error, never as
// "no tenant".
export async function fetchMyTenant(companyId, email, columns = "*") {
  if (!companyId) return { tenant: null, error: null };
  const { data: id, error: idErr } = await supabase.rpc("get_tenant_id", { p_company_id: companyId });
  if (!idErr && id) {
    const { data, error } = await supabase.from("tenants").select(columns).eq("company_id", companyId).eq("id", id).maybeSingle();
    if (error) return { tenant: null, error };
    if (data) return { tenant: data, error: null };
  }
  if (!email || !email.includes("@")) return { tenant: null, error: null };
  const { data: rows, error } = await supabase.from("tenants").select(columns).eq("company_id", companyId)
    .ilike("email", escapeFilterValue(String(email).trim().toLowerCase()))
    .is("archived_at", null).order("id", { ascending: false }).limit(1);
  if (error) return { tenant: null, error };
  return { tenant: (rows || [])[0] || null, error: null };
}
